import React, { useState, useEffect } from 'react';
import { Layout, Menu, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  GithubOutlined,
  StarOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FolderOutlined,
  UserOutlined,
  ScheduleOutlined,
  CloudUploadOutlined,
  SettingOutlined,
  FileSearchOutlined,
  KeyOutlined,
  ApiOutlined,
  CloudServerOutlined,
  GiftOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { systemAPI } from '../services/api';

const { Sider } = Layout;
const { Text } = Typography;

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

type MenuItem = Required<MenuProps>['items'][number];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [version, setVersion] = useState<string>('');

  const goTo = (path: string) => {
    navigate(path);
    if (window.innerWidth <= 992) {
      onCollapse(true);
    }
  };

  useEffect(() => {
    // 获取版本号
    const fetchVersion = async () => {
      try {
        const res = await systemAPI.getVersion();
        setVersion(res.data.version || 'unknown');
      } catch (error) {
        console.error('获取版本号失败', error);
        setVersion('unknown');
      }
    };
    fetchVersion();
  }, []);

  const menuItems: MenuItem[] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '仪表盘',
      onClick: () => goTo('/dashboard')
    },
    {
      key: '/checkin-quota',
      icon: <GiftOutlined />,
      label: '签到额度',
      onClick: () => goTo('/checkin-quota')
    },
    {
      key: '/quota-usage',
      icon: <LineChartOutlined />,
      label: '额度流水',
      onClick: () => goTo('/quota-usage')
    },
    {
      key: 'github',
      icon: <GithubOutlined />,
      label: 'GitHub管理',
      children: [
        {
          key: '/github/accounts',
          icon: <UserOutlined />,
          label: '账号管理',
          onClick: () => goTo('/github/accounts')
        },
        {
          key: '/github/groups',
          icon: <FolderOutlined />,
          label: '分组管理',
          onClick: () => goTo('/github/groups')
        },
        {
          key: '/github/repositories',
          icon: <StarOutlined />,
          label: '仓库收藏',
          onClick: () => goTo('/github/repositories')
        }
      ]
    },
    {
      key: 'automation',
      icon: <ClockCircleOutlined />,
      label: '自动化工具',
      children: [
        {
          key: '/automation/tasks',
          icon: <ScheduleOutlined />,
          label: '定时任务',
          onClick: () => goTo('/automation/tasks')
        },
        {
          key: '/automation/websites',
          icon: <GlobalOutlined />,
          label: 'API网站',
          onClick: () => goTo('/automation/websites')
        },
        {
          key: '/automation/push-settings',
          icon: <CloudUploadOutlined />,
          label: '密钥推送',
          onClick: () => goTo('/automation/push-settings')
        }
      ]
    },
    {
      key: '/settings/monitor',
      icon: <SettingOutlined />,
      label: '巡检设置',
      onClick: () => goTo('/settings/monitor')
    },
    {
      key: '/settings/solver',
      icon: <KeyOutlined />,
      label: '解算设置',
      onClick: () => goTo('/settings/solver')
    },
    {
      key: '/settings/network',
      icon: <ApiOutlined />,
      label: '网络设置',
      onClick: () => goTo('/settings/network')
    },
    {
      key: '/settings/proxy',
      icon: <CloudServerOutlined />,
      label: '出口代理',
      onClick: () => goTo('/settings/proxy')
    },
    {
      key: '/settings/display',
      icon: <FieldTimeOutlined />,
      label: '显示设置',
      onClick: () => goTo('/settings/display')
    },
    {
      key: '/logs',
      icon: <FileSearchOutlined />,
      label: '运行日志',
      onClick: () => goTo('/logs')
    }
  ];

  // 根据当前路径确定选中的菜单项和打开的子菜单
  const getSelectedKeys = () => {
    return [location.pathname];
  };

  const getOpenKeys = () => {
    if (location.pathname.startsWith('/github')) {
      return ['github'];
    }
    if (location.pathname.startsWith('/automation')) {
      return ['automation'];
    }
    return [];
  };

  return (
    <Sider
      className="app-sidebar"
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      breakpoint="lg"
      collapsedWidth={80}
      width={220}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'sticky',
        top: 0,
        left: 0,
        background: '#fff',
      }}
      trigger={
        collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
      }
    >
      <div className={`sidebar-brand${collapsed ? ' collapsed' : ''}`}>
        <GithubOutlined className="sidebar-brand-icon" />
        {!collapsed && <span>GitHub 管理</span>}
      </div>
      <Menu
        className="sidebar-menu"
        theme="light"
        mode="inline"
        selectedKeys={getSelectedKeys()}
        defaultOpenKeys={getOpenKeys()}
        items={menuItems}
        style={{ borderRight: 0 }}
      />
      <div style={{
        position: 'absolute',
        bottom: 16,
        left: 0,
        right: 0,
        padding: '0 16px',
        textAlign: 'center',
        color: '#8a959f'
      }}>
        {!collapsed && (
          <Text style={{ fontSize: 12, color: '#a0a9b1' }}>
            版本 {version}
          </Text>
        )}
      </div>
    </Sider>
  );
};

export default Sidebar;
